/**
 * OrderCancelControl — the «Отменить заказ» button + live countdown, or the post-window
 * hint (ТЗ §11). Presentation over a {@link SelfCancelState}; all logic lives in
 * {@link useSelfCancel}. Renders nothing when self-cancel is irrelevant to the order.
 */
import { ApiError } from "@shared/api";

import { formatCountdown } from "./lib";
import type { SelfCancelState } from "./useSelfCancel";
import styles from "./OrderCancelControl.module.css";

/** Text shown once the self-cancel window has elapsed (ТЗ §11). */
export const SELF_CANCEL_EXPIRED_TEXT =
  "Самостоятельная отмена уже недоступна. Напишите продавцу или в поддержку.";

export interface OrderCancelControlProps {
  readonly state: SelfCancelState;
  /** ``"compact"`` for the orders-list card; ``"panel"`` (default) for the order screen. */
  readonly variant?: "compact" | "panel";
}

function errorText(err: ApiError): string {
  const payload = err.payload as { readonly detail?: unknown } | null;
  if (payload !== null && typeof payload.detail === "string") {
    return payload.detail;
  }
  return "Не удалось отменить заказ. Попробуйте ещё раз.";
}

export function OrderCancelControl({
  state,
  variant = "panel",
}: OrderCancelControlProps): JSX.Element | null {
  const { isAvailable, isExpired, isCancelling, secondsLeft, error, cancel } = state;

  if (!isAvailable && !isExpired) {
    return null;
  }

  return (
    <div className={styles.wrap} data-variant={variant}>
      {error !== null ? (
        <p className={styles.error} role="alert">
          {errorText(error)}
        </p>
      ) : null}

      {isAvailable ? (
        <button
          type="button"
          className={styles.cancel}
          onClick={cancel}
          disabled={isCancelling}
        >
          {isCancelling ? (
            "Отменяем…"
          ) : (
            <>
              Отменить заказ
              <span className={styles.timer}>{formatCountdown(secondsLeft)}</span>
            </>
          )}
        </button>
      ) : (
        <p className={styles.expired} role="status">
          {SELF_CANCEL_EXPIRED_TEXT}
        </p>
      )}
    </div>
  );
}
